import type { AnimationDetailsDTO } from "../../dto/AnimationDetailsDTO";
import {
  AnimateActorAnimation,
  IAnimateActor,
} from "../../models/AnimateActor";
import { InteractionType } from "../../models/interactions/Interaction";

type AnimationDetailsResolver = (
  animationName: string
) => Promise<AnimationDetailsDTO | undefined>;

function collectLegacyAnimateActors(
  value: unknown,
  interactions: IAnimateActor[],
  visited: Set<object>
) {
  if (!value || typeof value !== "object" || visited.has(value)) return;
  visited.add(value);

  if (Array.isArray(value)) {
    value.forEach((item) =>
      collectLegacyAnimateActors(item, interactions, visited)
    );
    return;
  }

  const candidate = value as Partial<IAnimateActor>;
  if (
    candidate.type === InteractionType.AnimateActor &&
    typeof candidate.animation === "string" &&
    candidate.animation.trim()
  ) {
    interactions.push(candidate as IAnimateActor);
  }

  Object.values(value).forEach((item) =>
    collectLegacyAnimateActors(item, interactions, visited)
  );
}

export async function hydrateLegacyAnimateActorAnimations<T>(
  value: T,
  resolveAnimationDetails: AnimationDetailsResolver
): Promise<T> {
  const legacyInteractions: IAnimateActor[] = [];
  collectLegacyAnimateActors(value, legacyInteractions, new Set<object>());

  const animationNames = Array.from(
    new Set(legacyInteractions.map((interaction) => interaction.animation as string))
  );
  if (animationNames.length === 0) return value;

  const resolvedAnimations = new Map<string, AnimateActorAnimation>();
  await Promise.all(
    animationNames.map(async (name) => {
      try {
        const details = await resolveAnimationDetails(name);
        if (!details?.animationState) return;

        resolvedAnimations.set(name, {
          name: details.name || name,
          animationState: details.animationState,
        });
      } catch {
        // Preserve unresolved legacy strings so they remain backward compatible.
      }
    })
  );

  legacyInteractions.forEach((interaction) => {
    const legacyName = interaction.animation as string;
    const resolved = resolvedAnimations.get(legacyName);
    if (resolved) interaction.animation = resolved;
  });

  return value;
}
